import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import LabelsAPI from '../../api/labels';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { LABEL_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
  },
};

export const getters = {
  getLabels(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getLabelsOnSidebar(_state) {
    return _state.records
      .filter(record => record.show_on_sidebar)
      .sort((a, b) => a.title.localeCompare(b.title));
  },
};

export const actions = {
  revalidate: async function revalidate({ commit }, { newKey }) {
    try {
      const isExistingKeyValid = await LabelsAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await LabelsAPI.refetchAndCommit(newKey);
        commit(
          types.SET_LABELS,
          response.data.payload
            .map(Object.freeze)
            .sort((a, b) => a.title.localeCompare(b.title))
        );
      }
    } catch (error) {
      // Ignore error
    }
  },

  get: async function getLabels({ commit }) {
    commit(types.SET_LABEL_UI_FLAG, { isFetching: true });
    try {
      const response = await LabelsAPI.get(true);
      // Freeze each label so Vue 2 skips the per-property reactivity walk.
      // With 10k+ labels the getter/setter installation and dep tracking on
      // every property access (hot inside vue-multiselect's filter loop) is
      // what freezes the campaign audience modal on large accounts.
      const sortedLabels = response.data.payload
        .map(Object.freeze)
        .sort((a, b) => a.title.localeCompare(b.title));
      commit(types.SET_LABELS, sortedLabels);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createLabels({ commit }, cannedObj) {
    commit(types.SET_LABEL_UI_FLAG, { isCreating: true });
    try {
      const response = await LabelsAPI.create(cannedObj);
      AnalyticsHelper.track(LABEL_EVENTS.CREATE);
      commit(types.ADD_LABEL, Object.freeze(response.data));
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateLabels({ commit }, { id, ...updateObj }) {
    commit(types.SET_LABEL_UI_FLAG, { isUpdating: true });
    try {
      const response = await LabelsAPI.update(id, updateObj);
      AnalyticsHelper.track(LABEL_EVENTS.UPDATE);
      commit(types.EDIT_LABEL, Object.freeze(response.data));
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteLabels({ commit }, id) {
    commit(types.SET_LABEL_UI_FLAG, { isDeleting: true });
    try {
      await LabelsAPI.delete(id);
      AnalyticsHelper.track(LABEL_EVENTS.DELETED);
      commit(types.DELETE_LABEL, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isDeleting: false });
    }
  },

  syncCreated({ commit }, label) {
    if (!label?.id) {
      logInvalidPayload('syncCreated', label);
      return;
    }
    commit(types.UPSERT_LABEL, Object.freeze({ ...label }));
  },

  syncUpdated({ commit }, label) {
    if (!label?.id) {
      logInvalidPayload('syncUpdated', label);
      return;
    }
    commit(types.UPSERT_LABEL, Object.freeze({ ...label }));
  },

  syncDeleted({ commit }, id) {
    if (id == null) {
      logInvalidPayload('syncDeleted', { id });
      return;
    }
    commit(types.DELETE_LABEL, id);
  },

  syncImported({ dispatch }) {
    return dispatch('get');
  },
};

function logInvalidPayload(action, payload) {
  let serialized;
  try {
    serialized = JSON.stringify({
      event: 'tags_mfe_invalid_payload',
      action,
      payload,
    });
  } catch (_error) {
    serialized = JSON.stringify({
      event: 'tags_mfe_invalid_payload',
      action,
      payloadId: payload && typeof payload === 'object' ? payload.id : null,
      serializationError: 'unserializable_payload',
    });
  }
  // eslint-disable-next-line no-console
  console.warn(serialized);
}

export const mutations = {
  [types.SET_LABEL_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_LABELS]: MutationHelpers.set,
  [types.ADD_LABEL]: MutationHelpers.create,
  [types.UPSERT_LABEL]: MutationHelpers.setSingleRecord,
  [types.EDIT_LABEL]: MutationHelpers.update,
  [types.DELETE_LABEL]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
